import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { CancelTokenSource } from "axios";

export interface UploadProgress {
  percent: number;
  loaded: number;
  total: number;
  speed: number; // bytes per second
  startTime: number;
}

export interface UploadItem {
  id: string;
  fileName: string;
  folder: string;
  projectId?: string;
  rootPath?: string | null;
  uploadEventId?: string;
  progress: UploadProgress;
  status: "pending" | "uploading" | "completed" | "failed" | "cancelled" | "interrupted";
  error?: string;
  cancelSource?: CancelTokenSource;
  type?: "rawFile" | "library";
}

interface UploadStore {
  activeUploads: Record<string, UploadItem>;
  addUpload: (
    id: string,
    fileName: string,
    folder: string,
    cancelSource?: CancelTokenSource,
    type?: "rawFile" | "library",
    fileSize?: number,
    uploadEventId?: string,
    projectId?: string,
    rootPath?: string | null,
  ) => void;
  updateProgress: (id: string, loaded: number, total: number) => void;
  setComplete: (id: string) => void;
  setFailed: (id: string, error: string) => void;
  setCancelled: (id: string) => void;
  removeUpload: (id: string) => void;
  cancelUpload: (id: string) => void;
}

// Mark any in-progress uploads as interrupted since the browser refresh killed them
const handleRehydrate = (state?: UploadStore) => {
  if (!state) {
    return;
  }

  const updatedUploads = { ...state.activeUploads };
  let hasChanges = false;

  for (const id of Object.keys(updatedUploads)) {
    const upload = updatedUploads[id];
    if (!(upload.status === "uploading" || upload.status === "pending")) {
      continue;
    }

    updatedUploads[id] = {
      ...upload,
      status: "failed",
      error: "Upload interrupted by page refresh",
    };
    hasChanges = true;
  }

  if (hasChanges) {
    state.activeUploads = updatedUploads;
  }
};

export const useUploadStore = create<UploadStore>()(
  persist(
    (set, get) => ({
      activeUploads: {},

      addUpload: (id, fileName, folder, cancelSource, type = "rawFile", fileSize = 0, uploadEventId, projectId, rootPath) =>
        set((state) => ({
          activeUploads: {
            ...state.activeUploads,
            [id]: {
              id,
              fileName,
              folder,
              projectId,
              rootPath,
              uploadEventId,
              status: "pending",
              progress: {
                percent: 0,
                loaded: 0,
                total: fileSize,
                speed: 0,
                startTime: Date.now(),
              },
              cancelSource,
              type,
            },
          },
        })),

      updateProgress: (id, loaded, total) =>
        set((state) => {
          const upload = state.activeUploads[id];
          if (!upload) return state;

          const now = Date.now();
          const timeDiff = (now - upload.progress.startTime) / 1000; // seconds
          const speed = timeDiff > 0 ? loaded / timeDiff : 0;

          return {
            activeUploads: {
              ...state.activeUploads,
              [id]: {
                ...upload,
                status: "uploading" as const,
                progress: {
                  ...upload.progress,
                  percent: Math.round((loaded * 100) / (total || 1)),
                  loaded,
                  total,
                  speed,
                },
              },
            },
          };
        }),

      setComplete: (id) =>
        set((state) => {
          const upload = state.activeUploads[id];
          if (!upload) return state;

          return {
            activeUploads: {
              ...state.activeUploads,
              [id]: {
                ...upload,
                status: "completed" as const,
                progress: {
                  ...upload.progress,
                  percent: 100,
                  loaded: upload.progress.total,
                },
              },
            },
          };
        }),

      setFailed: (id, error) =>
        set((state) => {
          const upload = state.activeUploads[id];
          if (!upload) return state;

          return {
            activeUploads: {
              ...state.activeUploads,
              [id]: {
                ...upload,
                status: "failed" as const,
                error,
              },
            },
          };
        }),

      setCancelled: (id) =>
        set((state) => {
          const upload = state.activeUploads[id];
          if (!upload) return state;

          return {
            activeUploads: {
              ...state.activeUploads,
              [id]: {
                ...upload,
                status: "cancelled" as const,
              },
            },
          };
        }),

      removeUpload: (id) =>
        set((state) => {
          const remaining = { ...state.activeUploads };
          delete remaining[id];
          return { activeUploads: remaining };
        }),

      cancelUpload: (id) => {
        const upload = get().activeUploads[id];
        if (!upload) return;

        if (upload.cancelSource) {
          upload.cancelSource.cancel("User cancelled upload");
        }

        get().setCancelled(id);
      },
    }),
    {
      name: "sf-upload-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        ...state,
        activeUploads: Object.fromEntries(
          Object.entries(state.activeUploads).map(([id, item]) => [
            id,
            { ...item, cancelSource: undefined }, // Exclude non-serializable cancelSource
          ]),
        ),
      }),
      onRehydrateStorage: () => handleRehydrate,
    },
  ),
);
